import { Checkbox, Flex } from '@inflearn/ds-react-next';
import { useTranslation } from 'next-i18next';

import type { SignupMarketingConsentRequest } from '../../../../resources/@types/accountSchema';
import { Type } from '../my/settings/notification/constants';

export const MARKETING_CHANNELS = [
  Type.MARKETING_EMAIL,
  Type.MARKETING_KAKAO,
  Type.MARKETING_SMS,
] as const;

export type MarketingChannel = (typeof MARKETING_CHANNELS)[number];

export type MarketingChannelConsent = Record<MarketingChannel, boolean>;

export const createMarketingChannelConsent = (agreed: boolean): MarketingChannelConsent => ({
  [Type.MARKETING_EMAIL]: agreed,
  [Type.MARKETING_KAKAO]: agreed,
  [Type.MARKETING_SMS]: agreed,
});

export const INITIAL_MARKETING_CHANNEL_CONSENT = createMarketingChannelConsent(false);

export const isAllMarketingChannelsAgreed = (consent: MarketingChannelConsent) =>
  MARKETING_CHANNELS.every((channel) => consent[channel]);

export const isAnyMarketingChannelAgreed = (consent: MarketingChannelConsent) =>
  MARKETING_CHANNELS.some((channel) => consent[channel]);

export const toSignupMarketingConsent = (
  consent: MarketingChannelConsent,
): SignupMarketingConsentRequest => ({
  email: consent[Type.MARKETING_EMAIL],
  kakao: consent[Type.MARKETING_KAKAO],
  sms: consent[Type.MARKETING_SMS],
});

interface MarketingConsentCheckboxesProps {
  value: MarketingChannelConsent;
  onChange: (value: MarketingChannelConsent) => void;
}

/**
 * 광고성 정보 수신 동의를 매체(이메일·카카오톡·문자)별로 받는다.
 * 상위 체크박스는 매체 전체를 켜고 끄며, 일부만 동의하면 indeterminate로 표시한다.
 */
export function MarketingConsentCheckboxes({ value, onChange }: MarketingConsentCheckboxesProps) {
  const { t } = useTranslation();
  const isAllAgreed = isAllMarketingChannelsAgreed(value);
  const isAnyAgreed = isAnyMarketingChannelAgreed(value);

  const channelLabels: Record<MarketingChannel, string> = {
    [Type.MARKETING_EMAIL]: t('marketing_consent.channel.email'),
    [Type.MARKETING_KAKAO]: t('marketing_consent.channel.kakao'),
    [Type.MARKETING_SMS]: t('marketing_consent.channel.sms'),
  };

  return (
    <Flex direction="column" gap={8}>
      <Checkbox
        checked={isAllAgreed}
        indeterminate={isAnyAgreed && !isAllAgreed}
        onChange={(e) => onChange(createMarketingChannelConsent(e.currentTarget.checked))}
        label={t('(선택) 광고성 정보 수신 동의')}
        size="sm"
        radius="sm"
        py={5}
      />
      <Flex gap={16} pl={32}>
        {MARKETING_CHANNELS.map((channel) => (
          <Checkbox
            key={channel}
            checked={value[channel]}
            onChange={(e) => onChange({ ...value, [channel]: e.currentTarget.checked })}
            label={channelLabels[channel]}
            size="sm"
            radius="sm"
          />
        ))}
      </Flex>
    </Flex>
  );
}
